'use client';

import { useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import {
  ButtonBase,
  DigitCodeInput,
  NavigationBar,
  useHandleGqlErrorsWithSnackbar,
} from '../../../../common';
import { useAppNotification } from '@bts-web/core-features/notifications';
import {
  FilledSpinnerIos,
  RegularArrowLeft,
} from '../../../../common/svgIcons';
import { CloseDrawerButton } from '../../../../common/components/Drawer/CloseDrawerButton';
import { getWithdrawalMfaTranslations } from './getWithdrawalMfaTranslations';
import { getRoundedPrecision } from '@bts-web/utils-formatting';
import { createFiatWithdrawal } from './actions/createFiatWithdrawal.action';
import {
  wrapperStyles,
  bodyStyles,
  contentStyles,
  bottomSectionStyles,
} from '../../../../common/styles';
import {
  digitInputWrapperStyles,
  headingWrapperStyles,
  titleStyles,
  subtitleStyles,
  errorStyles,
} from './withdrawalMfaModalStyles';
import { PoweredByTextWithLogo } from '../../../Reusable/PoweredBy/PoweredBy';

interface WithdrawalMfaContentProps {
  amount: string;
  payoutAccountId: string;
  recipientName: string;
  tradeId?: string;
  onBack: () => void;
  onSuccess: (params: { amount: string | null; recipientName: string }) => void;
}

export const WithdrawalMfaContent = ({
  amount,
  payoutAccountId,
  recipientName,
  tradeId,
  onBack,
  onSuccess,
}: WithdrawalMfaContentProps) => {
  const { t } = useClientTranslation();

  const router = useRouter();

  const translations = getWithdrawalMfaTranslations(t);

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const { setAppNotification } = useAppNotification();

  const [code, setCode] = useState('');

  const [isCodeComplete, setIsCodeComplete] = useState(false);

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const [hasError, setHasError] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCodeChange = (isFilled: boolean, value: string) => {
    setIsCodeComplete(isFilled);

    setCode(value);

    if (!isFilled) {
      setHasError(false);
    }
  };

  const handleConfirm = async () => {
    setIsSubmitting(true);

    try {
      const result = await createFiatWithdrawal({
        amount,
        payoutAccountId,
        code,
        tradeId,
      });

      if (result.errors) {
        if (result.errors.includes('mfa.invalid')) {
          setHasError(true);

          return;
        }

        if (result.errors.includes('withdrawal.open.transaction')) {
          setAppNotification({
            visual: 'error',
            title: translations.withdrawalPendingErrorTitle,
            subtitle: translations.withdrawalPendingErrorSubtitle,
          });

          return;
        }

        processErrors(result.errors);

        return;
      }

      // createFiatWithdrawal is awaited imperatively (not via a form action /
      // transition), so its updateTag does not auto-refresh the mounted route.
      // Refresh the client router so the no-store fiat balance re-renders.
      router.refresh();

      const grossAmount = result.data?.amount;

      const fee = result.data?.fee;

      const netAmount =
        grossAmount && fee
          ? (
              Number(getRoundedPrecision(grossAmount)) -
              Number(getRoundedPrecision(fee))
            ).toFixed(2)
          : null;

      onSuccess({ amount: netAmount, recipientName });
    } catch {
      processErrors(['general_error_message_title']);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="withdrawal-mfa-modal" className={wrapperStyles}>
      <NavigationBar
        title={translations.navTitle}
        NavigationLeftSlot={
          <ButtonBase
            onClick={onBack}
            visual="ghost"
            size="small"
            aria-label="navigation back button"
            data-testid="navigation-back-button"
          >
            <RegularArrowLeft size={24} data-dir="rtl" />
          </ButtonBase>
        }
        NavigationRightSlot={<CloseDrawerButton />}
      />

      <div className={bodyStyles}>
        <div className={contentStyles}>
          <div className={headingWrapperStyles}>
            <span className={titleStyles}>{translations.title}</span>

            <span className={subtitleStyles}>{translations.subtitle}</span>
          </div>

          <div className={digitInputWrapperStyles}>
            <DigitCodeInput
              hasError={hasError}
              onChange={handleCodeChange}
              submitButtonRef={submitButtonRef}
            />

            {hasError && (
              <span className={errorStyles}>{translations.invalidCode}</span>
            )}
          </div>
        </div>

        <div className={bottomSectionStyles}>
          <PoweredByTextWithLogo noMargins />

          <ButtonBase
            ref={submitButtonRef}
            data-testid="withdrawal-mfa-confirm-button"
            visual="accent"
            size="large"
            onClick={handleConfirm}
            disabled={!isCodeComplete || isSubmitting}
            fullWidth
            noHover
          >
            {translations.confirm}
            {isSubmitting && (
              <FilledSpinnerIos
                aria-label="loading icon"
                data-element="loading-icon"
              />
            )}
          </ButtonBase>
        </div>
      </div>
    </div>
  );
};
